// src/client/renderer/vfx/tile/tile-vfx-manager.ts
//
// Plan 16, Task 1.1 — TileVfxManager skeleton.
//
// Design:
//   Structurally mirrors comic-vfx-manager.ts (per-pool active-set Map keyed by poolId,
//   closure-stored subscriberId, idempotent disposed flag).
//
//   CRITICAL DIVERGENCE from comic-vfx-manager's iteration shape:
//   - ComicVFXPool:  pool.update(emitter, dt)  [per-emitter]
//   - TileVfxPool:   pool.update(dt)           [per-pool, returns ReadonlySet of still-active emitters]
//
//   The tile manager calls pool.update(dt) ONCE per pool per frame, then walks the
//   returned ReadonlySet to detect finished emitters via pool.isFinished(emitter).
//   The manager maintains its own per-pool active sets so that activeCount stays accurate
//   even between update() calls (spawned-but-not-yet-updated emitters count as active).

import * as THREE from 'three';
import type {
  TileVfxPoolId,
  TileVfxPool,
  TileVfxSpawnArgs,
  TileVfxEventResolver,
  TileVfxManager,
  TileVfxManagerConfig,
} from './tile-vfx-types';
import type { GameEvent } from '../../../../shared/game/types';
import type { GsapRegistry } from '../gsap-registry';
import { createAmbientGlowPool } from './ambient-glow-pool';
import {
  createApproachHighlightPool,
  resolveApproachHighlightForEvent,
} from './approach-highlight-pool';
import { createPathPreviewLinePool, resolvePathPreviewForEvent } from './path-preview-line';
import { createLandingFxPool, resolveLandingFxForEvent } from './landing-fx-pool';
import { createTileBouncePool } from './tile-bounce';
import { createSoulAnchorRingPool, resolveSoulAnchorRingForEvent } from './soul-anchor-ring';
import { createEventTileTriggerPool, resolveEventTileTriggerForEvent } from './event-tile-trigger';
import { createWarpSwirlPool, resolveWarpSwirlForEvent } from './warp-swirl-pool';
import { createVoidFallPool, resolveVoidFallForEvent } from './void-fall-pool';
import { createItemTilePulsePool, resolveItemTilePulseForEvent } from './item-tile-pulse-pool';
import { createGoldenChestArrowPool, resolveGoldenChestArrowForEvent } from './golden-chest-arrow';

// ─────────────────────────────────────────────────────────────────────────────
// createTileVfxManager
// ─────────────────────────────────────────────────────────────────────────────

export function createTileVfxManager(config: TileVfxManagerConfig): TileVfxManager {
  const { scene, eventLog, pools, resolver } = config;

  // Per-pool active emitter tracking.
  // Manager-side set tracks emitters that have been added to the scene;
  // pool.update(dt) returns the authoritative still-active set from the pool's
  // perspective, which we use on each tick to discover newly-finished emitters.
  const activeByPool = new Map<TileVfxPoolId, Set<THREE.Object3D>>();
  for (const [poolId] of pools) {
    activeByPool.set(poolId, new Set<THREE.Object3D>());
  }

  let disposed = false;

  // ── Event subscriber ───────────────────────────────────────────────────────

  function handleEvent(event: GameEvent): void {
    const result = resolver(event);
    if (result === null) return;
    spawn(result.poolId, result.args);
  }

  const subscriberId = eventLog.subscribe(handleEvent);

  // ── spawn ──────────────────────────────────────────────────────────────────

  function spawn(poolId: TileVfxPoolId, args: TileVfxSpawnArgs): void {
    const pool = pools.get(poolId);
    if (!pool) return; // unknown poolId — no-op

    const emitter = pool.acquire(args);
    if (emitter === null) return; // pool full — no-op

    scene.add(emitter);

    let activeSet = activeByPool.get(poolId);
    if (!activeSet) {
      // Defensive: pool was added after construction (shouldn't normally happen)
      activeSet = new Set<THREE.Object3D>();
      activeByPool.set(poolId, activeSet);
    }
    activeSet.add(emitter);
  }

  // ── update ─────────────────────────────────────────────────────────────────

  function update(deltaSeconds: number): void {
    for (const [poolId, activeSet] of activeByPool) {
      const pool = pools.get(poolId);
      if (!pool) continue;

      // Per-pool update — returns the set of emitters STILL active after this tick.
      // This is the authoritative list; we do NOT call pool.update(emitter, dt) per-emitter.
      const stillActive = pool.update(deltaSeconds);

      // Walk the pool's returned active set to detect newly-finished emitters.
      // Any emitter in our manager-side set that is also reported by pool
      // but isFinished → remove + release.
      const toRemove: THREE.Object3D[] = [];
      for (const emitter of stillActive) {
        if (pool.isFinished(emitter)) {
          toRemove.push(emitter);
        }
      }
      // Also handle emitters in manager set that pool no longer reports at all
      // (pool already recycled them without going through isFinished=true path).
      for (const emitter of activeSet) {
        if (!stillActive.has(emitter) && !toRemove.includes(emitter)) {
          toRemove.push(emitter);
        }
      }

      for (const emitter of toRemove) {
        if (activeSet.has(emitter)) {
          scene.remove(emitter);
          pool.release(emitter);
          activeSet.delete(emitter);
        }
      }

      // Sync manager set to match what the pool says is still running
      // (add any emitters the pool reports but manager doesn't yet know about —
      //  this handles pools that track emitters internally without manager mediation)
      for (const emitter of stillActive) {
        if (!pool.isFinished(emitter) && !activeSet.has(emitter)) {
          activeSet.add(emitter);
        }
      }
    }
  }

  // ── activeCount ────────────────────────────────────────────────────────────

  function getActiveCount(): number {
    let total = 0;
    for (const activeSet of activeByPool.values()) {
      total += activeSet.size;
    }
    return total;
  }

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return; // idempotent guard
    disposed = true;

    // Unsubscribe from event bus
    eventLog.unsubscribe(subscriberId);

    // Call pool.dispose() on every pool (final GPU teardown) and clear active sets
    for (const [poolId, activeSet] of activeByPool) {
      const pool = pools.get(poolId);
      if (pool) {
        // Remove all live emitters from the scene before pool disposal
        for (const emitter of activeSet) {
          scene.remove(emitter);
        }
        pool.dispose();
      }
      activeSet.clear();
    }
  }

  return {
    spawn,
    update,
    get activeCount() {
      return getActiveCount();
    },
    dispose,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// createTileVfxPools — bundle factory for ALL 11 pools
// ─────────────────────────────────────────────────────────────────────────────
//
// CRITICAL F7 NOTE (Plan 16 Task 5.1 forward-defense):
//
// The tile-bounce pool is INCLUDED in this map because callers may need direct
// access to it. However, the manager-mediated `manager.spawn(...)` path MUST
// NOT be used for tile-bounce. The tile-bounce pool's "emitter" is the actual
// borrowed tileMarker Object3D (already parented to groups.board). Calling
// manager.spawn would invoke `scene.add(emitter)`, re-parenting the marker
// from groups.board to scene root — breaking the board hierarchy AND breaking
// subsequent scene.remove during release.
//
// Wire-up sites (build-board.ts, fixture HTML) MUST:
//   1. Use the COMPOSED resolver from createTileVfxEventResolver — which
//      DELIBERATELY OMITS tile-bounce.
//   2. Subscribe a SEPARATE handler to the eventLog that fires
//      `pools.get('tile_bounce')!.acquire(...)` directly (bypassing manager).
//
// See build-board.ts §Tile VFX wire-up for the canonical implementation.
//
// CRITICAL F6 NOTE: the tile-bounce pool has NO re-entry guard for mid-bounce
// re-acquire. Two landings on the same tile within 400 ms will leak the prior
// timeline and drift scale. The manager-mediated flow does NOT detect this
// for tile-bounce (since it never sees the events). Future plans should
// either: (a) gate `pool.isFinished(emitter)` on the wire-up before calling
// acquire, or (b) refactor the pool's acquire() to release the prior emitter
// when the same tileMarker is re-passed. Out of scope for this task.

/**
 * Bundle factory: returns ALL 11 tile-VFX pools instantiated with the shared
 * scene + GsapRegistry. Returned Map is frozen.
 *
 * Call site:
 *   const registry = createGsapRegistry();
 *   const pools = createTileVfxPools({ scene, registry });
 *   const resolver = createTileVfxEventResolver({ ... });
 *   const manager = createTileVfxManager({ scene, eventLog, pools, resolver });
 */
export function createTileVfxPools(deps: {
  readonly scene: THREE.Scene;
  readonly registry: GsapRegistry;
}): ReadonlyMap<TileVfxPoolId, TileVfxPool> {
  const { scene, registry } = deps;

  const pools = new Map<TileVfxPoolId, TileVfxPool>([
    ['ambient_glow', createAmbientGlowPool({ scene })],
    ['approach_highlight', createApproachHighlightPool({ scene, registry })],
    ['path_preview', createPathPreviewLinePool({ scene, registry })],
    ['landing_fx', createLandingFxPool({ scene, registry })],
    // tile_bounce included for direct-access via pools.get('tile_bounce') —
    // do NOT route through manager.spawn. See F7 note above.
    ['tile_bounce', createTileBouncePool({ registry })],
    ['soul_anchor_ring', createSoulAnchorRingPool({ scene, registry })],
    ['event_tile_trigger', createEventTileTriggerPool({ scene, registry })],
    ['warp_swirl', createWarpSwirlPool({ scene, registry })],
    ['void_fall', createVoidFallPool({ scene, registry })],
    ['item_tile_pulse', createItemTilePulsePool({ scene, registry })],
    ['golden_chest_arrow', createGoldenChestArrowPool({ scene, registry })],
  ]);

  return Object.freeze(pools);
}

// ─────────────────────────────────────────────────────────────────────────────
// createTileVfxEventResolver — composed resolver
// ─────────────────────────────────────────────────────────────────────────────
//
// Walks the per-pool resolvers in priority order (first-match-wins):
//   1. landing_fx          — LandingEvent (any known tileType)
//   2. soul_anchor_ring    — SoulAnchorClaimEvent
//   3. event_tile_trigger  — LandingEvent on event tiles
//   4. item_tile_pulse     — LandingEvent on item tiles
//   5. void_fall           — LandingEvent on void nodes (callback-gated)
//   6. warp_swirl          — MovementStepEvent over teleport connections
//   7. approach_highlight  — MovementStepEvent (any)
//   8. golden_chest_arrow  — EconomyChangeEvent (keys ≥ threshold)
//   9. path_preview        — currently always-null (Plan 19 owns)
//
// EXCLUSIONS (intentional):
//   - tile_bounce: handled via direct subscription in build-board.ts (F7).
//   - ambient_glow: spawned by wire-up at board-build time (no event resolver).

export interface CreateTileVfxEventResolverOpts {
  /** Plan 19 hook: resolve nodeId → world position. */
  readonly getPositionForNode?: (nodeId: string) => { x: number; y: number; z: number } | undefined;
  /** Plan 19 hook: classify a connection between two nodes. */
  readonly getConnectionType?: (
    fromNodeId: string,
    toNodeId: string,
  ) => 'walk' | 'jump' | 'teleport' | undefined;
  /** Plan 19 hook: identify void-node membership. */
  readonly isVoidNode?: (nodeId: string) => boolean;
  /** Plan 18/19 hook: chest-eligibility threshold (keys count). */
  readonly getChestEligibilityThreshold?: () => number;
}

/**
 * Bundle factory: returns the composed event resolver that walks all relevant
 * per-pool resolvers in priority order. NOT included: tile_bounce (F7 — see
 * createTileVfxPools leading note), ambient_glow (no event resolver).
 *
 * Position fields are normalised at this layer — when a per-pool resolver
 * supplies a {0,0,0} placeholder, this composer overrides it with the real
 * world position via getPositionForNode where available.
 */
export function createTileVfxEventResolver(
  opts: CreateTileVfxEventResolverOpts,
): TileVfxEventResolver {
  const { getPositionForNode, getConnectionType, isVoidNode, getChestEligibilityThreshold } = opts;

  return function resolveTileVfxEvent(
    event: GameEvent,
  ): { poolId: TileVfxPoolId; args: TileVfxSpawnArgs } | null {
    // Helper: lookup position by node id; returns undefined when the callback
    // is missing OR returns undefined.
    const lookupPos = (nodeId: string): { x: number; y: number; z: number } | undefined => {
      return getPositionForNode ? getPositionForNode(nodeId) : undefined;
    };

    // ── 1. landing_fx (LandingEvent — highest priority among landings) ──
    const landingFx = resolveLandingFxForEvent(event);
    if (landingFx !== null) {
      // event.type === 'landing' guaranteed by resolver
      const ev = event as Extract<GameEvent, { type: 'landing' }>;
      const pos = lookupPos(ev.nodeId);
      if (pos) {
        return {
          poolId: 'landing_fx',
          args: { ...landingFx.args, position: pos },
        };
      }
      return landingFx;
    }

    // ── 2. soul_anchor_ring (SoulAnchorClaimEvent) ──
    const soulAnchor = resolveSoulAnchorRingForEvent(event, getPositionForNode);
    if (soulAnchor !== null) return soulAnchor;

    // ── 3. event_tile_trigger (LandingEvent on event tiles) ──
    const eventTrigger = resolveEventTileTriggerForEvent(event);
    if (eventTrigger !== null) {
      const ev = event as Extract<GameEvent, { type: 'landing' }>;
      const pos = lookupPos(ev.nodeId);
      if (pos) {
        return {
          poolId: 'event_tile_trigger',
          args: { ...eventTrigger.args, position: pos },
        };
      }
      return eventTrigger;
    }

    // ── 4. item_tile_pulse (LandingEvent on item tiles) ──
    const itemPulse = resolveItemTilePulseForEvent(event);
    if (itemPulse !== null) {
      const ev = event as Extract<GameEvent, { type: 'landing' }>;
      const pos = lookupPos(ev.nodeId);
      if (pos) {
        return {
          poolId: 'item_tile_pulse',
          args: { ...itemPulse.args, position: pos },
        };
      }
      return itemPulse;
    }

    // ── 5. void_fall (LandingEvent gated on isVoidNode callback) ──
    const voidFall = resolveVoidFallForEvent(event, isVoidNode);
    if (voidFall !== null) {
      const ev = event as Extract<GameEvent, { type: 'landing' }>;
      const pos = lookupPos(ev.nodeId);
      if (pos) {
        return {
          poolId: 'void_fall',
          args: { ...voidFall.args, position: pos },
        };
      }
      return voidFall;
    }

    // ── 6. warp_swirl (MovementStepEvent over teleport connection) ──
    const warpSwirl = resolveWarpSwirlForEvent(event, getConnectionType);
    if (warpSwirl !== null) {
      const ev = event as Extract<GameEvent, { type: 'movement_step' }>;
      const pos = lookupPos(ev.toNodeId);
      if (pos) {
        return {
          poolId: 'warp_swirl',
          args: { ...warpSwirl.args, position: pos },
        };
      }
      return warpSwirl;
    }

    // ── 7. approach_highlight (MovementStepEvent — any movement step) ──
    const approachHighlight = resolveApproachHighlightForEvent(event, getPositionForNode);
    if (approachHighlight !== null) return approachHighlight;

    // ── 8. golden_chest_arrow (EconomyChangeEvent keys ≥ threshold) ──
    const chestArrow = resolveGoldenChestArrowForEvent(event, getChestEligibilityThreshold);
    if (chestArrow !== null) return chestArrow;

    // ── 9. path_preview (currently always returns null — Plan 19 owns) ──
    const pathPreview = resolvePathPreviewForEvent(event);
    if (pathPreview !== null) return pathPreview;

    return null;
  };
}
